import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import {
  getStoredTeamInviteSkip,
  ONBOARDING_TOTAL_STEPS,
  storeTeamInviteSkip,
  TEAM_ONBOARDING_TOTAL_STEPS,
} from "../../utils/proOnboarding";
import useProfessionalOnboardingProgress from "../../hooks/useProfessionalOnboardingProgress";
import authorizedRequest from "../../utils/api";
import { useActiveBusiness } from "../../hooks/useActiveBusiness";
import { resolveBookingPagePath } from "../../utils/bookingPageLink";
import { isCanceledRequest } from "../../utils/workspaceRequests";
import Modal from "../modal/Modal";
import BusinessTimeZoneConfirm from "./BusinessTimeZoneConfirm";
import "./ProfessionalOnboardingChecklist.css";

const ProfessionalOnboardingChecklist = ({
  userId = null,
  userUsername = null,
  onNavigate,
  onComplete,
  compact = false,
  onboardingState = null,
  teamMode = false,
  activeBusinessId = null,
  providerId = null,
  businessRole = null,
}) => {
  const location = useLocation();
  const { business: activeBusiness } = useActiveBusiness();
  const hasNotifiedCompletionRef = useRef(false);
  const isOnboardingRoute = location.pathname === "/onboarding" || location.pathname === "/onboarding/team";
  const [teamInviteSkipped, setTeamInviteSkipped] = useState(() =>
    getStoredTeamInviteSkip(userId, activeBusinessId)
  );
  const [teamInviteCreated, setTeamInviteCreated] = useState(false);
  const [skipPayoutConfirmationOpen, setSkipPayoutConfirmationOpen] = useState(false);

  useEffect(() => {
    setTeamInviteSkipped(getStoredTeamInviteSkip(userId, activeBusinessId));
  }, [activeBusinessId, userId]);

  useEffect(() => {
    if (!teamMode || !userId) {
      setTeamInviteCreated(false);
      return undefined;
    }
    const businessId = activeBusinessId;
    if (!businessId) return undefined;
    const controller = new AbortController();
    authorizedRequest({
      url: `/api/team/businesses/${businessId}/team`,
      signal: controller.signal,
    }).then((payload) => {
      if (controller.signal.aborted) return;
      const hasInvite = (payload?.invites || []).some((invite) =>
        ["pending", "accepted"].includes(invite?.status)
      );
      const hasTeammate = (payload?.members || []).some((member) => member?.role !== "owner");
      setTeamInviteCreated(hasInvite || hasTeammate);
    }).catch((error) => {
      if (!isCanceledRequest(error, controller.signal)) setTeamInviteCreated(false);
    });
    return () => controller.abort();
  }, [activeBusinessId, teamMode, userId]);

  const isReadOnly = !userId;

  const localOnboardingState = useProfessionalOnboardingProgress({
    userId,
    userUsername,
    enabled: !onboardingState,
    activeBusinessId,
    providerId,
    teamMode,
    businessRole,
  });

  const resolvedState = onboardingState || localOnboardingState;
  const steps = resolvedState?.steps || {};
  const loading = Boolean(resolvedState?.loading);
  const error = resolvedState?.error || "";
  const handleRetry = resolvedState?.refresh;
  const actions = useMemo(() => resolvedState?.actions || {}, [resolvedState?.actions]);

  const {
    completedCount,
    profileSetupComplete,
    workLocationSetupComplete,
    canShareProfile,
    isComplete,
    progress,
  } = resolvedState?.progressMeta || {
    completedCount: 0,
    profileSetupComplete: false,
    workLocationSetupComplete: false,
    canShareProfile: false,
    isComplete: false,
    progress: 0,
  };
  const totalSteps = resolvedState?.progressMeta?.totalSteps || (
    steps.teamSettingsRequired
      ? TEAM_ONBOARDING_TOTAL_STEPS
      : ONBOARDING_TOTAL_STEPS
  );

  useEffect(() => {
    if (loading || error || !isComplete) {
      hasNotifiedCompletionRef.current = false;
      return;
    }

    if (hasNotifiedCompletionRef.current || typeof onComplete !== "function") {
      return;
    }

    hasNotifiedCompletionRef.current = true;
    onComplete();
  }, [error, isComplete, loading, onComplete]);

  const handleNavigate = useCallback(
    (path, options) => {
      if (typeof onNavigate === "function") {
        onNavigate(path || null, options);
      }
    },
    [onNavigate]
  );

  const getOnboardingReturnOptions = useCallback(
    () =>
      isOnboardingRoute
        ? {
            state: {
              fromOnboarding: true,
              returnToOnboarding: true,
              onboardingReturnPath: teamMode ? "/onboarding/team" : "/onboarding",
            },
          }
        : undefined,
    [isOnboardingRoute, teamMode]
  );

  const handleShareProfile = useCallback(() => {
    const bookingPagePath = resolveBookingPagePath(activeBusiness, {
      username: userUsername,
      _id: userId,
    });
    if (!bookingPagePath) {
      return;
    }

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const profileUrl = `${origin}${bookingPagePath}`;

    if (origin) {
      window.open(profileUrl, "_blank", "noopener,noreferrer");
    }

    if (navigator?.clipboard?.writeText && profileUrl) {
      navigator.clipboard.writeText(profileUrl).catch(() => {
        /* Ignore clipboard errors */
      });
    }
    handleNavigate(null);
  }, [activeBusiness, handleNavigate, userId, userUsername]);

  const handleCompleteShareProfile = useCallback(() => {
    if (typeof actions.markShareProfileComplete === "function") {
      actions.markShareProfileComplete();
    }
  }, [actions]);

  const handleSkipPayouts = useCallback(() => {
    setSkipPayoutConfirmationOpen(true);
  }, []);

  const handleConfirmSkipPayouts = useCallback(() => {
    if (typeof actions.skipPayoutSetup === "function") {
      actions.skipPayoutSetup();
    }
    setSkipPayoutConfirmationOpen(false);
  }, [actions]);

  const handleSkipTeamInvite = useCallback(() => {
    storeTeamInviteSkip(true, userId, activeBusinessId);
    setTeamInviteSkipped(true);
  }, [activeBusinessId, userId]);

  const stepsConfig = useMemo(
    () => [
      ...(steps.teamSettingsRequired
        ? [
            {
              id: "teamSettings",
              title: "Set your team settings",
              description:
                "Choose payment routing, provider assignments, permissions, and team emails.",
              cta: steps.teamSettingsDraftSaved ? "Finish team settings" : "Set team settings",
              completedCta: "Review team settings",
              onClick: () => handleNavigate("/onboarding/team-settings"),
              completed: steps.teamSettings,
              draftSaved: steps.teamSettingsDraftSaved,
              badge: steps.teamSettingsDraftSaved && !steps.teamSettings
                ? {
                    label: "Draft saved",
                    tone: "muted",
                  }
                : null,
            },
          ]
        : []),
      {
        id: "services",
        title: "List your first service",
        description: "Add a service so customers know what you offer.",
        cta: "Add service",
        completedCta: "Add another service",
        onClick: () => handleNavigate("/add-service", getOnboardingReturnOptions()),
        completed: steps.services,
      },
      {
        id: "availability",
        title: "Set your availability",
        description: "Choose when clients can book time with you.",
        cta: "Set availability",
        completedCta: "Update your availability",
        onClick: () => handleNavigate("/availability", getOnboardingReturnOptions()),
        completed: steps.availability,
      },
      {
        id: "workLocationSetup",
        title: "Set your business details",
        description:
          "Add business details, your booking link, your time zone, and where appointments happen.",
        cta: "Set business details",
        completedCta: "Update business details",
        onClick: () => handleNavigate("/onboarding/business-details"),
        completed: workLocationSetupComplete,
      },
      {
        id: "profileSetup",
        title: "Build your booking page",
        description:
          "Add your introduction, portfolio, and page style.",
        cta: "Open Booking Page Builder",
        completedCta: "Edit booking page",
        onClick: () => handleNavigate("/booking-page-builder"),
        completed: profileSetupComplete,
      },
      {
        id: "bookingQuestions",
        title: "Set booking questions",
        description: "Collect prep notes and preferences before a visit.",
        cta: "Manage questions",
        completedCta: "Update questions",
        onClick: () => handleNavigate("/booking-questions"),
        completed: steps.bookingQuestions,
        badge: {
          label: "Optional",
          tone: "muted",
        },
      },
      {
        id: "profileGuidelines",
        title: "Set your confirmation message",
        description:
          "Share preparation, arrival, or appointment instructions clients receive after booking.",
        cta: "Edit confirmation message",
        completedCta: "Edit confirmation message",
        onClick: () => handleNavigate("/pro/settings/booking-messages#after-booking-message"),
        completed: steps.profileGuidelines,
        badge: {
          label: "Optional",
          tone: "muted",
        },
      },
      {
        id: "payouts",
        title: steps.teamPaymentContext?.title || "Accept online payments",
        description:
          steps.teamPaymentContext?.description ||
          "Connect Stripe so clients can pay securely when booking.",
        cta: "Set up payments",
        onClick: () => handleNavigate(
          steps.teamPaymentContext?.ctaPath || "/payments/connect",
        ),
        completed: steps.teamPaymentRequired
          ? steps.payouts
          : steps.payouts || steps.payoutsSkipped,
        skipped: !steps.teamPaymentRequired && steps.payoutsSkipped && !steps.payouts,
        completedCta:
          steps.payoutsSkipped && !steps.payouts ? "Set up payments" : "Manage payouts",
        onSkip: steps.teamPaymentRequired ? null : handleSkipPayouts,
        badge: steps.teamPaymentRequired
          ? null
          : {
              label: "Optional",
              tone: "muted",
            },
      },
      {
        id: "shareProfile",
        title: "Share your profile",
        description: "Send your public link to potential clients.",
        completed: steps.shareProfile,
      },
      ...(teamMode
        ? [
            {
              id: "inviteTeam",
              title: "Invite your team",
              description: "Bring teammates into the shared workspace and choose their roles.",
              cta: "Invite teammates",
              completedCta: "Manage team",
              onClick: () => handleNavigate("/team"),
              onSkip: handleSkipTeamInvite,
              completed: teamInviteCreated || teamInviteSkipped,
              skipped: teamInviteSkipped && !teamInviteCreated,
              badge: {
                label: teamInviteCreated ? "Optional / Invited" : "Optional",
                tone: "muted",
              },
            },
          ]
        : []),
    ],
    [
      handleNavigate,
      steps.availability,
      steps.payouts,
      steps.payoutsSkipped,
      steps.bookingQuestions,
      steps.profileGuidelines,
      profileSetupComplete,
      workLocationSetupComplete,
      steps.services,
      steps.shareProfile,
      handleSkipPayouts,
      getOnboardingReturnOptions,
      handleSkipTeamInvite,
      teamInviteSkipped,
      teamInviteCreated,
      teamMode,
      steps.teamSettings,
      steps.teamSettingsDraftSaved,
      steps.teamSettingsRequired,
      steps.teamPaymentContext,
      steps.teamPaymentRequired,
    ]
  );

  const showTimeZoneControl = Boolean(
    !isReadOnly &&
      steps.businessTimeZone &&
      steps.confirmedTimeZone !== steps.businessTimeZone &&
      steps.canManageBusinessTimeZone &&
      typeof actions.confirmBusinessTimeZone === "function" &&
      typeof actions.changeBusinessTimeZone === "function"
  );

  // W11 distinguishes launch-critical tasks from the useful follow-ups. Keep
  // the existing completion model, but make that distinction visible here.
  const requiredStepIds = new Set([
    "teamSettings",
    "services",
    "availability",
    "workLocationSetup",
    "profileSetup",
  ]);
  const requiredSteps = stepsConfig.filter((step) => requiredStepIds.has(step.id));
  const optionalSteps = stepsConfig.filter((step) => !requiredStepIds.has(step.id));
  const completedRequiredCount = requiredSteps.filter((step) => step.completed).length;
  const requiredProgress = requiredSteps.length
    ? Math.round((completedRequiredCount / requiredSteps.length) * 100)
    : 0;
  const nextRequiredStep = requiredSteps.find(
    (step) => !step.completed && typeof step.onClick === "function"
  );
  const businessName = activeBusiness?.name || activeBusiness?.businessName || "your business";

  return (
    <section
      className={`pro-onboarding-card${compact ? " pro-onboarding-card--compact" : " pro-onboarding-card--getting-started"}`}
      aria-live="polite"
    >
      <div className="pro-onboarding-card__header">
        <h3>{compact ? "Onboarding checklist" : `Getting ${businessName} ready`}</h3>
        <p>
          {compact
            ? `${completedCount} of ${totalSteps} steps completed`
            : `${completedRequiredCount} of ${requiredSteps.length} done`}
        </p>
      </div>

      <div
        className="pro-onboarding-card__progress"
        role="progressbar"
        aria-valuenow={compact ? progress : requiredProgress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="pro-onboarding-card__progress-bar"
          style={{ width: `${compact ? progress : requiredProgress}%` }}
        />
      </div>

      {!compact && completedRequiredCount > 0 && nextRequiredStep && (
        <button
          type="button"
          className="pro-onboarding-card__guided-setup"
          onClick={nextRequiredStep.onClick}
        >
          Pick up guided setup
        </button>
      )}

      {isReadOnly && (
        <p className="pro-onboarding-card__hint">
          Sign in to track these tasks automatically.
        </p>
      )}

      {error && (
        <div className="pro-onboarding-card__alert" role="alert">
          <span>{error}</span>
          <button
            type="button"
            onClick={handleRetry}
            className="pro-onboarding-card__retry"
            disabled={typeof handleRetry !== "function"}
          >
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="pro-onboarding-card__loading">Checking your onboarding status...</div>
      ) : (
        <ul className="pro-onboarding-card__steps">
          {stepsConfig.map((step) => {
            const isShareStep = step.id === "shareProfile";
            const isPayoutStep = step.id === "payouts";
            const isTeamInviteStep = step.id === "inviteTeam";
            const isRequiredStep = requiredStepIds.has(step.id);
            const isFirstInGroup = isRequiredStep
              ? requiredSteps[0]?.id === step.id
              : optionalSteps[0]?.id === step.id;
            const isLastInGroup = isRequiredStep
              ? requiredSteps[requiredSteps.length - 1]?.id === step.id
              : optionalSteps[optionalSteps.length - 1]?.id === step.id;
            const isDisabled = loading || step.completed;
            const hasPrimaryAction = typeof step.onClick === "function";
            const isPrimaryActionDisabled = loading || !hasPrimaryAction;
            const isStatusActionEnabled = !step.completed && !loading && hasPrimaryAction;

            const shareLocked = isShareStep && !canShareProfile;
            const shareStatus =
              isShareStep && !step.completed && !canShareProfile && !loading
                ? teamMode
                  ? "Complete your service, availability, business details, and booking page to unlock"
                  : "Complete the first four steps to unlock"
                : "";
            const badgeTone = step.skipped
              ? "muted"
              : !step.completed
                ? step.badge?.tone
                : null;
            const badgeLabel = step.skipped
              ? "Optional / Skipped"
              : !step.completed
                ? step.badge?.label
                : null;

            return (
              <React.Fragment key={step.id}>
                {!compact && isFirstInGroup && (
                  <li className="pro-onboarding-card__section-heading" role="presentation">
                    <h4>{isRequiredStep ? "Needed before clients can book" : "Nice to have"}</h4>
                  </li>
                )}
                <li
                  className={`pro-onboarding-card__step${step.completed ? " is-complete" : ""}${
                    !compact ? ` pro-onboarding-card__step--${isRequiredStep ? "required" : "optional"}` : ""
                  }${!compact && isFirstInGroup ? " is-first-in-group" : ""}${
                    !compact && isLastInGroup ? " is-last-in-group" : ""
                  }`}
                >
                <div className="pro-onboarding-card__step-top">
                  <div className="pro-onboarding-card__step-main">
                    {isStatusActionEnabled ? (
                      <button
                        type="button"
                        className="pro-onboarding-card__step-status pro-onboarding-card__step-status--interactive"
                        onClick={step.onClick}
                        aria-label={`Go to ${step.title}`}
                      >
                        {isShareStep && !userId ? "-" : ""}
                      </button>
                    ) : (
                      <span className="pro-onboarding-card__step-status" aria-hidden="true">
                        {step.completed ? "✓" : isShareStep && !userId ? "-" : ""}
                      </span>
                    )}
                    <div>
                      <p className="pro-onboarding-card__step-title">{step.title}</p>
                      <p className="pro-onboarding-card__step-description">{shareStatus || step.description}</p>
                    </div>
                  </div>
                  {badgeTone && badgeLabel ? (
                    <span
                      className={`pro-onboarding-card__step-badge pro-onboarding-card__step-badge--${badgeTone}`}
                    >
                      {badgeLabel}
                    </span>
                  ) : null}
                </div>
                {step.id === "workLocationSetup" && showTimeZoneControl && (
                  <BusinessTimeZoneConfirm
                    timeZone={steps.businessTimeZone}
                    confirmed={steps.confirmedTimeZone === steps.businessTimeZone}
                    disabled={loading}
                    onConfirm={actions.confirmBusinessTimeZone}
                    onChange={actions.changeBusinessTimeZone}
                  />
                )}
                {isShareStep ? (
                  <div className="pro-onboarding-card__step-actions">
                    <button
                      type="button"
                      className="pro-onboarding-card__step-action"
                      onClick={handleShareProfile}
                      disabled={shareLocked || loading}
                    >
                      View profile
                    </button>
                    <button
                      type="button"
                      className="pro-onboarding-card__step-action"
                      onClick={handleCompleteShareProfile}
                      disabled={shareLocked || isDisabled}
                    >
                      {step.completed ? "Completed" : "Mark as complete"}
                    </button>
                  </div>
                ) : isPayoutStep || isTeamInviteStep ? (
                  <div className="pro-onboarding-card__step-actions">
                    <button
                      type="button"
                      className="pro-onboarding-card__step-action"
                      onClick={step.onClick}
                      disabled={isPrimaryActionDisabled}
                    >
                      {step.completed ? step.completedCta || step.cta : step.cta}
                    </button>
                    {!step.completed && typeof step.onSkip === "function" && (
                      <button
                        type="button"
                        className="pro-onboarding-card__step-action pro-onboarding-card__step-action--secondary"
                        onClick={step.onSkip}
                        disabled={loading || isReadOnly}
                      >
                        {isTeamInviteStep ? "Do this later" : "Skip for now"}
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="pro-onboarding-card__step-action"
                    onClick={step.onClick}
                    disabled={isPrimaryActionDisabled}
                  >
                    {step.completed ? step.completedCta || step.cta : step.cta}
                  </button>
                )}
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      )}

      <Modal
        open={skipPayoutConfirmationOpen}
        onClose={() => setSkipPayoutConfirmationOpen(false)}
        title="Skip online payments for now?"
      >
        <div className="pro-onboarding-card__skip-payment-confirmation">
          <p>
            You can still accept bookings, but clients will need to pay you in cash. You
            won&apos;t be able to accept online payments until you connect Stripe.
          </p>
          <div className="pro-onboarding-card__skip-payment-actions">
            <button
              type="button"
              className="pro-onboarding-card__step-action pro-onboarding-card__step-action--secondary"
              onClick={() => setSkipPayoutConfirmationOpen(false)}
            >
              Go back
            </button>
            <button
              type="button"
              className="pro-onboarding-card__step-action pro-onboarding-card__skip-payment-confirm"
              onClick={handleConfirmSkipPayouts}
            >
              Yes, use cash payments
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
};

ProfessionalOnboardingChecklist.propTypes = {
  userId: PropTypes.string,
  userUsername: PropTypes.string,
  onNavigate: PropTypes.func,
  onComplete: PropTypes.func,
  compact: PropTypes.bool,
  teamMode: PropTypes.bool,
  activeBusinessId: PropTypes.string,
  providerId: PropTypes.string,
  businessRole: PropTypes.string,
  onboardingState: PropTypes.shape({
    steps: PropTypes.object,
    progressMeta: PropTypes.shape({
      completedCount: PropTypes.number,
      totalSteps: PropTypes.number,
      profileSetupComplete: PropTypes.bool,
      workLocationSetupComplete: PropTypes.bool,
      canShareProfile: PropTypes.bool,
      isComplete: PropTypes.bool,
      progress: PropTypes.number,
    }),
    loading: PropTypes.bool,
    error: PropTypes.string,
    refresh: PropTypes.func,
    actions: PropTypes.shape({
      markShareProfileComplete: PropTypes.func,
      skipPayoutSetup: PropTypes.func,
      confirmBusinessTimeZone: PropTypes.func,
      changeBusinessTimeZone: PropTypes.func,
    }),
  }),
};

export default ProfessionalOnboardingChecklist;
